/**
 * Organizations API Client
 *
 * API client functions for all organization-related endpoints.
 * Extends BaseApiClient for authentication, error handling, and type-safe requests.
 */

// GW-6533: Validate orgId at API client boundary so frontend regressions
// throw a distinct InvalidOrgIdError before the network call leaves the
// browser, instead of producing a noisy 4xx after a round-trip.
// GW-6534: Read currentOrg.id from the Zustand store as the canonical source
// of truth, eliminating direct localStorage reads.
import { getCurrentOrgIdFromStore } from '@/stores/orgIdAccessor';
import { assertOrgId } from '@/types/orgId';

import { ApiError } from './apiError';
import { BaseApiClient } from './baseApiClient';

import type {
  Organization,
  OrganizationSettings,
  OrganizationMember,
  OrganizationUsageMetrics,
  OrganizationInvitation,
  InvitationStatus,
  InvitationDetails,
} from '@gw/ui/types';

class OrganizationsApiClient extends BaseApiClient {
  /**
   * GW-6534: Read the current org id from the Zustand store rather than
   * localStorage. Throws ApiError 400 if no org is selected; throws
   * InvalidOrgIdError if the persisted value somehow becomes malformed.
   */
  private requireOrgId(): string {
    const orgId = getCurrentOrgIdFromStore();
    if (!orgId) {
      throw new ApiError('No organization selected', 400);
    }
    assertOrgId(orgId);
    return orgId;
  }

  // ============================================================================
  // Organization Management APIs
  // ============================================================================

  /**
   * Get all organizations the current user belongs to
   *
   * GET /users/me/organizations
   * Authorization: Any authenticated user
   *
   * @returns Array of organizations the user is a member of
   * @throws {ApiError} When API request fails
   */
  async getUserOrganizations(): Promise<Organization[]> {
    try {
      const response = await this.get<{ organizations: Organization[] }>('/users/me/organizations');
      return response.organizations;
    } catch (error) {
      console.error('[OrganizationsApiClient] Failed to fetch organizations:', error);
      throw error;
    }
  }

  /**
   * Get details of a specific organization by ID
   *
   * GET /organizations/{orgId}
   * Authorization: User must be a member of the organization
   *
   * @param orgId - The organization ID to retrieve
   * @returns Organization details including roles and settings
   * @throws {ApiError} When API request fails or org not found
   */
  async getOrganization(orgId: string): Promise<Organization> {
    assertOrgId(orgId);
    return this.get<Organization>(`/organizations/${orgId}`);
  }
  /**
   * Get the current user's selected organization
   *
   * GET /organizations/me
   * Authorization: Any authenticated user
   *
   * @returns Current organization details
   * @throws {ApiError} When API request fails or no org selected
   */
  async getCurrentOrganization(): Promise<Organization> {
    return this.get<Organization>('/organizations/me');
  }
  /**
   * Update organization details
   *
   * PUT /organizations/{orgId}
   * Authorization: org_admin role required
   *
   * @param orgId - The organization ID to update
   * @param updates - Partial organization data to update
   * @returns Updated organization object
   * @throws {ApiError} When API request fails or insufficient permissions
   */
  async updateOrganization(orgId: string, updates: Partial<Organization>): Promise<Organization> {
    assertOrgId(orgId);
    return this.put<Organization>(`/organizations/${orgId}`, updates);
  }
  // ============================================================================
  // Organization Settings APIs
  // ============================================================================

  /**
   * Get organization settings
   *
   * GET /organizations/me/settings
   * Authorization: org_admin or org_member
   *
   * @returns Organization settings including preferences and limits
   * @throws {ApiError} When API request fails
   */
  async getOrganizationSettings(): Promise<OrganizationSettings> {
    return this.get<OrganizationSettings>('/organizations/me/settings');
  }
  /**
   * Update organization settings
   *
   * PUT /organizations/me/settings
   * Authorization: org_admin role required
   *
   * @param settings - Organization settings to update
   * @returns Updated organization settings
   * @throws {ApiError} When API request fails or insufficient permissions
   */
  async updateOrganizationSettings(settings: OrganizationSettings): Promise<OrganizationSettings> {
    return this.put<OrganizationSettings>('/organizations/me/settings', settings);
  }
  // ============================================================================
  // Organization Members APIs
  // ============================================================================

  /**
   * Get all members of the current organization
   *
   * GET /organizations/me/members
   * Authorization: org_admin or org_member
   *
   * @returns Array of organization members with roles and status
   * @throws {ApiError} When API request fails
   */
  async getOrganizationMembers(): Promise<OrganizationMember[]> {
    const response = await this.get<{ members: OrganizationMember[] }>('/organizations/me/members');
    return response.members;
  }
  /**
   * Invite a new member to the organization
   *
   * POST /organizations/{org_id}/members/invite
   * Authorization: org_admin role required
   *
   * @param email - Email address of the user to invite
   * @param role - Role to assign to the new member (org_admin, org_member, app_admin)
   * @throws {ApiError} When API request fails or email already invited
   */
  async inviteMember(email: string, role: string): Promise<void> {
    const orgId = this.requireOrgId();
    return this.post(`/organizations/${orgId}/members/invite`, { email, role });
  }
  /**
   * Update a member's role in the organization (single role - legacy)
   *
   * PUT /organizations/me/members/{userId}/role
   * Authorization: org_admin role required
   *
   * @param userId - The user ID of the member to update
   * @param role - New role to assign (org_admin, org_member, app_admin)
   * @throws {ApiError} When API request fails or insufficient permissions
   * @deprecated Use updateMemberRoles for multi-role support
   */
  async updateMemberRole(userId: string, role: string): Promise<void> {
    return this.put(`/organizations/me/members/${userId}/role`, { role });
  }

  /**
   * Update a member's roles in the organization (multi-role support)
   *
   * PUT /organizations/me/members/{userId}/role
   * Authorization: org_admin role required
   *
   * The backend will automatically ensure org_member is always included.
   *
   * @param userId - The user ID of the member to update
   * @param roles - Array of roles to assign (org_admin, org_member, app_admin, dataset_admin)
   * @throws {ApiError} When API request fails or insufficient permissions
   */
  async updateMemberRoles(userId: string, roles: string[]): Promise<void> {
    return this.put(`/organizations/me/members/${userId}/role`, { roles });
  }
  /**
   * Remove a member from the organization
   *
   * DELETE /organizations/me/members/{userId}
   * Authorization: org_admin role required
   *
   * @param userId - The user ID of the member to remove
   * @throws {ApiError} When API request fails, user is self, or last admin
   */
  async removeMember(userId: string): Promise<void> {
    return this.delete(`/organizations/me/members/${userId}`);
  }

  /**
   * Update a member's status in the organization
   *
   * PATCH /organizations/{orgId}/members/{userId}/status
   * Authorization: org_admin role required
   *
   * @param orgId - The organization UUID (the status route does not accept the literal 'me')
   * @param userId - The user ID of the member to update
   * @param status - New status to set ('active' | 'suspended')
   * @throws {ApiError} When API request fails or insufficient permissions
   */
  async updateMemberStatus(
    orgId: string,
    userId: string,
    status: 'active' | 'suspended'
  ): Promise<void> {
    return this.patch(`/organizations/${orgId}/members/${userId}/status`, { status });
  }

  // ============================================================================
  // Organization Invitations APIs
  // ============================================================================

  /**
   * Validate an invitation code
   *
   * GET /invitations/validate?code={code}
   * Authorization: None (public endpoint for signup flow)
   *
   * @param code - The invitation code from the URL
   * @returns Invitation details including email, org name, role, and validity
   * @throws {ApiError} When API request fails
   */
  async validateInvitation(code: string): Promise<InvitationDetails> {
    try {
      // Public endpoint - no auth required
      const response = await fetch(
        `${this.baseURL}/invitations/validate?code=${encodeURIComponent(code)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(
          errorData.error || errorData.message || `Failed to validate invitation`,
          response.status,
          errorData
        );
      }

      return response.json();
    } catch (error) {
      console.error('[OrganizationsApiClient] Failed to validate invitation:', error);
      throw error;
    }
  }

  /**
   * Accept an invitation after successful signup
   *
   * POST /invitations/accept
   * Authorization: Bearer token required (newly created user)
   *
   * @param code - The invitation code to accept
   * @throws {ApiError} When API request fails
   */
  async acceptInvitation(code: string): Promise<void> {
    // GW-513: Backend expects 'invitationCode' field per AcceptInvitationRequest struct
    return this.post('/invitations/accept', { invitationCode: code });
  }

  /**
   * Get pending invitations for the authenticated user
   *
   * GET /users/me/invitations
   * Authorization: Bearer token required
   *
   * GW-411: Used to prompt existing users to accept invitations on login
   *
   * @returns Array of pending invitations with org details
   * @throws {ApiError} When API request fails
   */
  async getUserPendingInvitations(): Promise<{
    invitations: Array<{
      invitationId: string;
      invitationCode: string;
      organizationId: string;
      orgName: string;
      email: string;
      role: string;
      status: string;
      expiresAt: number;
      createdAt: string;
    }>;
    count: number;
  }> {
    return this.get('/users/me/invitations');
  }

  /**
   * Get all invitations for the current organization
   *
   * GET /organizations/{org_id}/invitations
   * Authorization: org_admin role required
   *
   * @param status - Optional status filter (pending, accepted, expired, revoked)
   * @returns Array of organization invitations with details
   * @throws {ApiError} When API request fails
   */
  async getOrganizationInvitations(
    status?: InvitationStatus
  ): Promise<{ invitations: OrganizationInvitation[]; totalCount: number }> {
    const orgId = this.requireOrgId();
    const params = status ? this.buildQueryString({ status }) : '';
    return this.get<{ invitations: OrganizationInvitation[]; totalCount: number }>(
      `/organizations/${orgId}/invitations${params}`
    );
  }

  /**
   * Resend invitation email
   *
   * POST /organizations/{org_id}/invitations/{invitationId}/resend
   * Authorization: org_admin role required
   *
   * @param invitationId - The invitation ID to resend
   * @returns Confirmation with sent timestamp and signup URL
   * @throws {ApiError} When API request fails or invitation already accepted/expired
   */
  async resendInvitation(
    invitationId: string
  ): Promise<{ invitationId: string; emailSent: boolean; signupUrl: string; expiresAt: number }> {
    const orgId = this.requireOrgId();
    return this.post<{
      invitationId: string;
      emailSent: boolean;
      signupUrl: string;
      expiresAt: number;
    }>(`/organizations/${orgId}/invitations/${invitationId}/resend`);
  }

  /**
   * Revoke an invitation
   *
   * DELETE /organizations/{org_id}/invitations/{invitationId}
   * Authorization: org_admin role required
   *
   * @param invitationId - The invitation ID to revoke
   * @returns Confirmation with revoked timestamp
   * @throws {ApiError} When API request fails
   */
  async revokeInvitation(
    invitationId: string
  ): Promise<{ invitationId: string; status: string; revokedAt: string }> {
    const orgId = this.requireOrgId();
    return this.delete<{ invitationId: string; status: string; revokedAt: string }>(
      `/organizations/${orgId}/invitations/${invitationId}`
    );
  }

  // ============================================================================
  // Organization Search APIs
  // ============================================================================

  async searchOrganizations(
    query: string,
    limit = 20
  ): Promise<{ orgId: string; orgName: string }[]> {
    const params = this.buildQueryString({ q: query, limit: String(limit) });
    const response = await this.get<{ items: { orgId: string; orgName: string }[] }>(
      `/v1/organizations/search${params}`
    );
    return response.items;
  }

  // ============================================================================
  // Organization Usage APIs
  // ============================================================================

  /**
   * Get organization usage metrics and token balance
   *
   * GET /organizations/me/usage
   * Authorization: org_admin or org_member
   *
   * @returns Usage metrics including token balance and monthly usage
   * @throws {ApiError} When API request fails
   */
  async getOrganizationUsage(): Promise<OrganizationUsageMetrics> {
    return this.get<OrganizationUsageMetrics>('/organizations/me/usage');
  }
}

// Export singleton instance
export const organizationsApi = new OrganizationsApiClient();
